import {
  leaveSession,
  muteParticipant,
  pauseParticipantVideo,
} from '../handlers';
import {
  hideParticipantList,
  lowerHandLocal,
  muteAudioTrack,
  type Participant,
  pauseVideoTrack,
  pinParticipant,
  raisedHandLocal,
  resumeVideoTrack,
  setChatButtonUnreadCount,
  setLayout,
  showParticipantList,
  startRecording,
  stopRecording,
  switchCamera,
  toggleAudioTrack,
  toggleParticipantList,
  toggleRaiseHand,
  toggleRecording,
  toggleVideoTrack,
  unMuteAudioTrack,
  unpinParticipant,
} from '../store';
import { type Layout } from '../types';

export class SessionMethodsCore {
  /**
   * Mutes the local user's audio during the call.
   */
  public static muteAudio() {
    muteAudioTrack();
  }

  /**
   * Unmutes the local user's audio during the call.
   */
  public static unmuteAudio() {
    unMuteAudioTrack();
  }

  /**
   * Toggles the local user's audio mute state.
   * If audio is muted, it will be unmuted, and vice versa.
   */
  public static toggleAudio() {
    toggleAudioTrack();
  }

  /**
   * Pauses the local user's video stream.
   */
  public static pauseVideo() {
    pauseVideoTrack();
  }

  /**
   * Resumes the local user's video stream.
   */
  public static resumeVideo() {
    resumeVideoTrack();
  }

  /**
   * Toggles the local user's video stream.
   * If video is paused, it will be resumed, and vice versa.
   */
  public static toggleVideo() {
    toggleVideoTrack();
  }

  /**
   * Local user leaves the current session.
   */
  public static leaveSession() {
    leaveSession();
  }

  /**
   * Raises the user's virtual hand in the call.
   */
  public static raiseHand() {
    raisedHandLocal();
  }

  /**
   * Lowers the user's virtual hand in the call.
   */
  public static lowerHand() {
    lowerHandLocal();
  }

  /**
   * Toggles the user's virtual hand state.
   * If the hand is raised, it will be lowered, and vice versa.
   */
  public static toggleHand() {
    toggleRaiseHand();
  }

  /**
   * Switches between the front and rear camera.
   */
  public static switchCamera() {
    switchCamera();
  }

  /**
   * Sets the layout type for the call.
   * @param layout - The type of layout to set (tile, sidebar or spotlight).
   */
  public static setLayout(layout: Layout) {
    setLayout(layout);
  }

  /**
   * Starts recording the call.
   */
  public static startRecording() {
    startRecording();
  }

  /**
   * Stops the ongoing call recording.
   */
  public static stopRecording() {
    stopRecording();
  }

  /**
   * Toggles the call recording state.
   * If recording is active, it will be stopped, and vice versa.
   */
  public static toggleRecording() {
    toggleRecording();
  }

  /**
   * Pins a participant's video to focus on them.
   * @param participantId - The ID of the participant to pin.
   * @param type - The type of the participant.
   */
  public static pinParticipant(
    participantId: string,
    type: Participant['type'],
  ) {
    pinParticipant(participantId, type);
  }

  /**
   * Unpins a participant's video.
   */
  public static unpinParticipant() {
    unpinParticipant();
  }

  /**
   * Mutes the audio of a specific participant.
   * @param participantId - The ID of the participant to mute.
   */
  public static muteParticipant(participantId: string) {
    muteParticipant(participantId);
  }

  /**
   * Pauses the video stream of a specific participant.
   * @param participantId - The ID of the participant whose video to pause.
   */
  public static pauseParticipantVideo(participantId: string) {
    pauseParticipantVideo(participantId);
  }

  /**
   * Sets the unread message count displayed on the chat button.
   * @param count - The number of unread messages.
   */
  public static setChatButtonUnreadCount(count: number) {
    setChatButtonUnreadCount(count);
  }

  /**
   * Toggles the visibility of the participant list panel.
   */
  public static toggleParticipantList() {
    toggleParticipantList();
  }

  /**
   * Shows the participant list panel.
   */
  public static showParticipantList() {
    showParticipantList();
  }

  /**
   * Hides the participant list panel.
   */
  public static hideParticipantList() {
    hideParticipantList();
  }

  // Legacy Methods

  // /**
  //  * @deprecated use setLayout() instead
  //  */
  // public static setMode(
  //   mode: (typeof CallConstants.MODE)[keyof typeof CallConstants.MODE],
  // ) {
  //   const layout = mode === 'DEFAULT' ? 'SIDEBAR' : mode;
  //   this.setLayout(layout);
  // }

  /**
   * @deprecated switchToVideoCall is deprecated and not supported.
   */
  public static switchToVideoCall() {
    console.error('switchToVideoCall method deprecated and not supported.');
  }

  /**
   * @deprecated use leaveSession() instead
   */
  public static endSession() {
    this.leaveSession();
  }

  /**
   * Retrieves call details for a given session ID and auth token.
   * @param sessionID - The session ID of the call.
   * @param authToken - The authentication token required to make the API call.
   * @returns A Promise that resolves to a CallLog object containing the call details.
   * @throws {CometChatCallsException} If there is an error retrieving the call details.
   * @deprecated This method is deprecated and not supported.
   */
  public static getCallDetails(_sessionID: string, _authToken: string) {
    throw new Error('This method is deprecated and not supported.');
  }
}
